/***
 * Created on 2025/4/29
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useReducer} from 'react';
import ic_content_site from '../../static/img/content/ic-content-site.svg';
import {useTranslation} from "react-i18next";
import CloudSpaceOrganizationUsage from "./CloudSpaceOrganizationUsage";
import CloudSpaceUserSpaceLimitComponent from "./CloudSpaceUserSpaceLimitComponent";
import {useQuery} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useOrganization} from "../../context/OrganizationContext";
import CloudSpaceEmptyPageComponent from "./CloudSpaceEmptyPageComponent";
import OrgContentPlaceholderComponent from "../organization-content/OrgContentPlaceholderComponent";
import {useOutletContext} from "react-router-dom";
import CloudSpaceStatsChartComponent from "./CloudSpaceStatsChartComponent";
import CloudSpaceRegionStatsToolbarComponent from "./CloudSpaceRegionStatsToolbarComponent";
import useToggle from "../../hook/ToggleHook";

function reducerHandler(state, action) {
    switch (action.type) {
        case 'set_tab':
            return {...state, current_tab: action.payload};
        case 'set_region':
            return {
                ...state,
                current_region: {
                    ...action.payload,
                    top_users: Array.from({length: 5}, (_,i) => action.payload?.top_usage?.users?.slice(0,5)[i] ?? null),
                    top_spaces: Array.from({length: 5}, (_,i) => action.payload?.top_usage?.spaces?.slice(0,5)[i] ?? null)
                }
            };
        default:
            return state;
    }
}

function CloudSpaceIndexComponent(props) {
    const {userPermissions} = useOutletContext();
    const hasPermission = userPermissions?.hasPermission('manage_organization_settings');
    const isReadOnly = userPermissions?.role === 'user_manager';
    const {t} = useTranslation();
    const {OrganizationAPI} = useOrganizationApi();
    const {getCurrentOrg, userProfile} = useOrganization();
    const [open, node] = useToggle();
    const org = getCurrentOrg();

    const [state, dispatch] = useReducer(reducerHandler, {
        current_tab: 'organization_usage'
    });


    const onTabClicked = (tab_name) => {
        dispatch({type: 'set_tab', payload: tab_name});
    }
    const {data, error, isError, isFetched} = useQuery({
        queryKey: ['cloud_space_index', org?.organization_id],
        queryFn: async () => {
            const response = await OrganizationAPI.q2.get(`org_storage/${org?.organization_id}/profile?statistics=true`)
            let space_profile = response.result;
            space_profile['data'] = space_profile?.region_infos;
            delete space_profile.region_infos;
            if (space_profile?.exception_user_ids && space_profile?.exception_user_ids.length > 0) {
                // org.users may be absent when currentOrg was set from the
                // dashboard payload (no users[]). Default to empty array so
                // .find() doesn't throw and break the whole query.
                const orgUsers = org?.users || [];
                space_profile.exception_user_ids = space_profile?.exception_user_ids.map((item) => {
                    const member = orgUsers.find((user) => {
                        if (user.id === item) {
                            return user;
                        }
                        return null;
                    });
                    return member;
                });
            }
            if (space_profile?.data && space_profile?.data.length > 0) {
                dispatch({
                    type: 'set_region',
                    payload: space_profile.data.sort((a, b) => a.region.localeCompare(b.region))[0]
                });
            }
            return space_profile;
        },
    });

    // Set initial region after data is fetched
    useEffect(() => {
        if (isFetched && data?.data?.length > 0 && !state.current_region) {
            dispatch({
                type: 'set_region',
                payload: data.data.sort((a, b) => a.region.localeCompare(b.region))[0]
            });
        }
    }, [isFetched, data, state.current_region]);

    return (
        <div className="content content-page">
            <div className="content-title">
                <div className="title-ic"><img src={ic_content_site} alt=""/></div>
                <div className="title-label">{t('@Cloud Space@')}</div>
            </div>

            <div className="tabbar">
                <ul>
                    <li className={`tab-item ${state.current_tab === 'organization_usage' ? "active" : ""}`} onClick={e => onTabClicked('organization_usage')}>{t('@Organization Usage@')}</li>
                    <li className={`tab-item ${state.current_tab === 'space_limit' ? "active" : ""}`} onClick={e => onTabClicked('space_limit')}>{t('@Settings@')}</li>
                </ul>
                <div className="comp-select comp-select--tab-select auto-width drop-adjustable" ref={node}>
                    <div className="select-toggle min-w-0">
                        <span className="select-label">{state.current_tab === 'organization_usage' ? t('@Organization Usage@') : t('@Settings@')}</span>
                    </div>
                    <div className={`comp-dropdown ${open}`}>
                        <ul className="comp-dropdown-menu">
                            <li className="section">
                                <div className="section-items selection" onClick={e => onTabClicked('organization_usage')}>{t('@Organization Usage@')}</div>
                                <div className="section-items selection" onClick={e => onTabClicked('space_limit')}>{t('@User Sapce Limit@')}</div>
                            </li>
                        </ul>
                    </div>
                </div>
                {!isReadOnly &&
                    <div className="ml-auto">
                        <div className="q-btn q-btn-default q-btn-round-corner q-btn-sys-blue q-btn-tiny !ml-8"
                             onClick={e => window.open(`${window._env_.REACT_APP_Q2_UPGRADE_URL}`, '_blank')}
                        >{t('@Upgrade@')}</div>
                    </div>
                }
            </div>

            {state.current_tab === 'organization_usage' && data &&
                <CloudSpaceRegionStatsToolbarComponent usage_data={data} state={state} dispatch={dispatch}/>
            }

            <div className="content-wrapper page-wrapper">

                {!isFetched && <OrgContentPlaceholderComponent/>}
                {isFetched && (!data || (!hasPermission && !isReadOnly)) && <CloudSpaceEmptyPageComponent/>}

                {
                    data &&
                    <>
                        {state.current_tab === 'organization_usage' &&
                            <>
                                <CloudSpaceStatsChartComponent usage_data={data} state={state} dispatch={dispatch}/>
                                <CloudSpaceOrganizationUsage usage_data={data} isReadOnly={isReadOnly}/>
                            </>
                        }

                        {state.current_tab === 'space_limit' && <CloudSpaceUserSpaceLimitComponent usage_data={data} isReadOnly={isReadOnly}/>}
                    </>
                }


            </div>
        </div>
    );
}

export default CloudSpaceIndexComponent;